import { useState, useRef, useEffect, useCallback } from 'react';
import { Sparkles, ChevronDown, Menu, User, Coins } from 'lucide-react';
import {
  Tooltip,
  ProfileDropdown,
  HeaderMenu,
  FeedbackModal,
  PulsingDots,
  OrientationToggle,
} from '@/components/ui';
import { CartButton } from '@/features/cart/components/CartButton';
import { AuthPrompt, ProfileSettingsModal } from '@/features/auth';
import { CreditsModal } from '@/features/credits';
import { useCart, useAuth } from '@/context';
import { useCredits, usePWAInstall } from '@/hooks';
import { isSupabaseConfigured } from '@/config/supabase';
import { signOut } from '@/services/auth';
import { formatPrice } from '@/utils/currency';
import type { Product } from '@/types';
import type { Orientation } from '@/types/orientation.types';
import styles from './Header.module.css';

export interface HeaderProps {
  title?: string;
  /** Currently selected product (for dropdown) */
  selectedProduct?: Product | null;
  /** All available products */
  products?: Product[];
  /** Callback when a product is selected from dropdown */
  onProductChange?: (productId: string) => void;
  /** Callback to navigate to home/product selection */
  onGoHome?: () => void;
  /** Callback to open shortcuts panel */
  onShowShortcuts?: () => void;
  /** Callback to open cart modal */
  onOpenCart?: () => void;
  /** Callback to open order history modal */
  onShowOrderHistory?: () => void;
  /** Disable product switching (e.g., during AI generation) */
  disableProductChange?: boolean;
  /** Current canvas orientation */
  orientation?: Orientation;
  /** Callback when orientation changes */
  onOrientationChange?: (orientation: Orientation) => void;
}

export function Header({
  title = 'Laser Tag Studio',
  selectedProduct,
  products = [],
  onProductChange,
  onGoHome,
  onShowShortcuts,
  onOpenCart,
  onShowOrderHistory,
  disableProductChange = false,
  orientation,
  onOrientationChange,
}: HeaderProps) {
  const { getItemCount } = useCart();
  const { user, isLoading: authLoading } = useAuth();
  const { credits, loading: creditsLoading, refetch: refetchCredits } = useCredits();
  const { canInstall, isInstalled, promptInstall } = usePWAInstall();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'profile' | 'orderHistory' | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const profileRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close product dropdown when clicking outside
  useEffect(() => {
    if (!isDropdownOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  // Reset focused index when dropdown closes
  useEffect(() => {
    if (!isDropdownOpen) {
      setFocusedIndex(-1);
    }
  }, [isDropdownOpen]);

  // Focus the first option when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && products.length > 0) {
      setFocusedIndex(0);
    }
  }, [isDropdownOpen, products.length]);

  // Move DOM focus to the currently focused option
  useEffect(() => {
    if (!isDropdownOpen || focusedIndex < 0) return;
    const listbox = dropdownRef.current?.querySelector('[role="listbox"]');
    if (!listbox) return;
    const options = listbox.querySelectorAll<HTMLElement>('[role="option"]');
    options[focusedIndex]?.focus();
  }, [isDropdownOpen, focusedIndex]);

  // Keyboard navigation for product dropdown
  const handleDropdownKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isDropdownOpen || products.length === 0) return;

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          setFocusedIndex((prev) => (prev + 1) % products.length);
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          setFocusedIndex((prev) => (prev - 1 + products.length) % products.length);
          break;
        }
        case 'Home': {
          event.preventDefault();
          setFocusedIndex(0);
          break;
        }
        case 'End': {
          event.preventDefault();
          setFocusedIndex(products.length - 1);
          break;
        }
        case 'Escape': {
          event.preventDefault();
          setIsDropdownOpen(false);
          triggerRef.current?.focus();
          break;
        }
        // Enter/Space are handled natively by the focused button
        default:
          break;
      }
    },
    [isDropdownOpen, products.length]
  );

  // Close header menu when clicking outside (includes trigger button via menuRef)
  useEffect(() => {
    if (!isMenuOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const handleProductSelect = (productId: string) => {
    onProductChange?.(productId);
    setIsDropdownOpen(false);
  };

  const handleProfileClick = () => {
    // Wait for auth to finish loading before checking user state
    if (authLoading) {
      return;
    }
    // Check if auth is required
    if (isSupabaseConfigured() && !user) {
      setPendingAction('profile');
      setShowAuthPrompt(true);
      return;
    }
    // Toggle profile dropdown if user is logged in
    setIsProfileOpen(!isProfileOpen);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsProfileOpen(false);
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  const handleOrderHistoryClick = () => {
    // Wait for auth to finish loading before checking user state
    if (authLoading) {
      return;
    }
    // Check if auth is required
    if (isSupabaseConfigured() && !user) {
      setPendingAction('orderHistory');
      setShowAuthPrompt(true);
      return;
    }
    onShowOrderHistory?.();
  };

  const handleAuthSuccess = useCallback(() => {
    setShowAuthPrompt(false);
    if (pendingAction === 'profile') {
      setIsProfileOpen(true);
    } else if (pendingAction === 'orderHistory') {
      onShowOrderHistory?.();
    }
    setPendingAction(null);
  }, [pendingAction, onShowOrderHistory]);

  const handleAuthClose = useCallback(() => {
    setShowAuthPrompt(false);
    setPendingAction(null);
  }, []);

  return (
    <header className={styles.header}>
      {/* Left: Logo/Brand */}
      <div className={styles.left}>
        {onGoHome ? (
          <button
            type="button"
            className={styles.brandButton}
            onClick={onGoHome}
            aria-label="Go to product selection"
          >
            <svg
              className={styles.backArrow}
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <Sparkles className={styles.icon} size={24} aria-hidden="true" />
            <span className={styles.title}>{title}</span>
          </button>
        ) : (
          <div className={styles.brand}>
            <Sparkles className={styles.icon} size={24} aria-hidden="true" />
            <span className={styles.title}>{title}</span>
          </div>
        )}
      </div>

      {/* Center: Product Dropdown (only shown when products available) */}
      {products.length > 0 && (
        <div className={styles.center} ref={dropdownRef} onKeyDown={handleDropdownKeyDown}>
          <button
            type="button"
            ref={triggerRef}
            className={styles.productDropdown}
            onClick={() => !disableProductChange && setIsDropdownOpen(!isDropdownOpen)}
            disabled={disableProductChange}
            aria-expanded={isDropdownOpen}
            aria-haspopup="listbox"
          >
            <span className={styles.productName}>{selectedProduct?.name || 'Select Product'}</span>
            <ChevronDown
              size={18}
              className={`${styles.chevron} ${isDropdownOpen ? styles.chevronOpen : ''}`}
              aria-hidden="true"
            />
          </button>

          {isDropdownOpen && (
            <div
              className={styles.dropdownMenu}
              role="listbox"
              aria-activedescendant={
                focusedIndex >= 0 && products[focusedIndex]
                  ? `product-option-${products[focusedIndex].id}`
                  : undefined
              }
            >
              {products.map((product, index) => (
                <button
                  key={product.id}
                  id={`product-option-${product.id}`}
                  type="button"
                  className={`${styles.dropdownItem} ${
                    selectedProduct?.id === product.id ? styles.dropdownItemActive : ''
                  }`}
                  onClick={() => handleProductSelect(product.id)}
                  role="option"
                  aria-selected={selectedProduct?.id === product.id}
                  tabIndex={index === focusedIndex ? 0 : -1}
                >
                  <img
                    src={product.frontPreviewImage}
                    alt=""
                    className={styles.productThumbnail}
                    aria-hidden="true"
                  />
                  <div className={styles.productInfo}>
                    <span className={styles.productItemName}>{product.name}</span>
                    <span className={styles.productCategory}>{formatPrice(product.price)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Orientation Toggle - only when product is selected and orientation props are provided */}
      {selectedProduct && orientation && onOrientationChange && (
        <OrientationToggle orientation={orientation} onToggle={onOrientationChange} />
      )}

      {/* Right: Menu, Profile, Cart */}
      <nav className={styles.actions} aria-label="Header actions">
        <div ref={menuRef} className={styles.menuWrapper}>
          <Tooltip content="Menu" position="bottom">
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Open menu"
              aria-expanded={isMenuOpen}
              aria-haspopup="menu"
            >
              <Menu size={20} aria-hidden="true" />
            </button>
          </Tooltip>
          <HeaderMenu
            isOpen={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
            onGoHome={onGoHome}
            onShowShortcuts={onShowShortcuts}
            onShowOrderHistory={handleOrderHistoryClick}
            onShowFeedback={() => setShowFeedbackModal(true)}
          />
        </div>

        {user && (
          <span
            className={styles.coinCount}
            aria-label={creditsLoading ? 'Loading credits' : `${credits} credits`}
          >
            <Coins size={16} aria-hidden="true" />
            <span>{creditsLoading ? <PulsingDots label="Loading credits" /> : credits}</span>
          </span>
        )}

        <div ref={profileRef} className={styles.menuWrapper}>
          <Tooltip content="Profile" position="bottom">
            <button
              type="button"
              className={styles.actionButton}
              onClick={handleProfileClick}
              aria-label="View profile"
              aria-expanded={isProfileOpen}
              aria-haspopup="menu"
            >
              <User size={20} aria-hidden="true" />
            </button>
          </Tooltip>
          {user && (
            <ProfileDropdown
              user={user}
              onSignOut={handleSignOut}
              isOpen={isProfileOpen}
              onClose={() => setIsProfileOpen(false)}
              onOpenSettings={() => setShowProfileSettings(true)}
              canInstallPWA={canInstall && !isInstalled}
              onInstallPWA={promptInstall}
              credits={credits}
              creditsLoading={creditsLoading}
              onOpenCredits={() => setShowCreditsModal(true)}
            />
          )}
        </div>

        <CartButton itemCount={getItemCount()} onClick={() => onOpenCart?.()} />
      </nav>

      <AuthPrompt
        isOpen={showAuthPrompt}
        onClose={handleAuthClose}
        onSuccess={handleAuthSuccess}
        message={
          pendingAction === 'profile'
            ? 'Sign in to view and manage your profile'
            : 'Sign in to view your order history'
        }
      />

      <CreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        onPurchaseComplete={() => {
          refetchCredits();
          setShowCreditsModal(false);
        }}
      />

      <ProfileSettingsModal
        isOpen={showProfileSettings}
        onClose={() => setShowProfileSettings(false)}
      />

      <FeedbackModal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} />
    </header>
  );
}
